import { dehydrate, QueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import Image from 'next/image';
import { useState } from 'react';
import { MdSearchOff } from 'react-icons/md';

import Faq from '@/components/Core/Faq/Faq';
import SectionTitle from '@/components/Core/SectionTitle/SectionTitle';
import MainLayout from '@/components/Layout/Main';
import { CustomMeta } from '@/components/Meta/CustomMeta';
import Button from '@/components/Primitive/Button/Button';
import SearchField from '@/components/Primitive/SearchField/SearchField';
import SmartLink from '@/components/Primitive/SmartLink';
import Spinner from '@/components/Primitive/Spinner/Spinner';
import useDebounce from '@/hooks/use-debounce';
import usePagination, {
  makeQuery as makePaginatedQuery,
} from '@/hooks/use-pagination';
import useQuery, { makeQuery } from '@/hooks/use-query';
import {
  createFaqCategoriesQuery,
  mapToFaqCategories,
} from '@/queries/faqs/categories';
import {
  createFaqsListQuery,
  mapToPaginatedFaqsList,
} from '@/queries/faqs/list';
import {
  createFaqsPageQuery,
  mapToFaqsPageContent,
} from '@/queries/globals/faqs';

const faqsPageQuery = createFaqsPageQuery();

const faqsListQuery = createFaqsListQuery();
const faqCategoriesQuery = createFaqCategoriesQuery();

const FaqsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const debouncedSearchTerm = useDebounce<string>(searchTerm, 500);

  const { data: pageData } = useQuery(faqsPageQuery, {}, mapToFaqsPageContent);

  const { data: categoriesData } = useQuery(
    faqCategoriesQuery,
    {},
    mapToFaqCategories
  );

  const faqsListQueryVars = {
    ...(debouncedSearchTerm && { searchTerm: debouncedSearchTerm }),
    ...(selectedCategory && { category: selectedCategory }),
  };
  const { data, isFetchingNextPage, loading, fetchMore, hasMore } =
    usePagination(faqsListQuery, faqsListQueryVars, {
      formatFn: mapToPaginatedFaqsList,
      limit: 7,
    });

  const { docs: faqs } = data || {};

  const handleCategorySelect = (value: string) => {
    if (value === selectedCategory) {
      setSelectedCategory(null);
      return;
    }

    setSelectedCategory(value);
  };

  const categories = categoriesData
    ? [...categoriesData, { id: '', name: 'All' }]
    : [];

  return (
    <MainLayout className="pt-14 md:pt-16">
      <CustomMeta {...pageData?.seo} />
      <div className="pb-page space-y-8 overflow-x-hidden md:space-y-20">
        <div className="relative mx-auto">
          <div className="relative aspect-[5/3] md:aspect-[5/2]">
            {pageData?.heroImage && (
              <Image
                src={pageData?.heroImage}
                fill
                alt=""
                className="object-cover"
              />
            )}
          </div>
        </div>

        <div className="container-md flex flex-wrap gap-2 font-light text-cream">
          Filter by:
          <div className="flex flex-wrap gap-2">
            {categories?.map((c, i) => {
              return (
                <>
                  <button
                    className={clsx(
                      'font-medium text-cream hover:underline',
                      selectedCategory === c.id && 'text-offWhite underline'
                    )}
                    key={c.id}
                    onClick={() => handleCategorySelect(c.id)}
                  >
                    {c.name}
                  </button>
                  {i !== categories.length - 1 && (
                    <span className="font-medium text-cream">\</span>
                  )}
                </>
              );
            })}
          </div>
        </div>

        <div className="container-md flex flex-col items-center justify-center space-y-10">
          <SectionTitle intro="SUPPORt" title="FAQS" />
          <SearchField
            className="w-full max-w-[340px]"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="container-md flex flex-col items-center">
          <div className="w-full">
            {faqs?.map((f) => {
              return <Faq answer={f.answer} question={f.question} key={f.id} />;
            })}
          </div>
          {!isFetchingNextPage && !loading && !hasMore && faqs.length === 0 && (
            <p className="flex flex-col items-center justify-center text-cream">
              <MdSearchOff className="h-14 w-14" />
              Could not find any FAQs
            </p>
          )}
          {loading && <Spinner className="mt-4 h-16 w-16 text-cream" />}
          {hasMore && (
            <Button
              loading={isFetchingNextPage}
              className="mt-8 uppercase"
              onClick={() => fetchMore()}
            >
              View More
            </Button>
          )}
        </div>

        <div className="container-md flex flex-col items-center justify-center space-y-5 text-center">
          <div className="text-xl font-black text-cream md:text-2xl">
            Can&apos;t find the answers you need?
          </div>
          <SmartLink
            to="/contact-us"
            className="text-xl font-black text-cream underline hover:text-pink-500 focus:text-pink-500 md:text-2xl"
          >
            Get in touch
          </SmartLink>
        </div>
      </div>
    </MainLayout>
  );
};

export default FaqsPage;

export const getStaticProps = async () => {
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery(['query', faqsPageQuery, {}], async () => {
    return makeQuery(faqsPageQuery, {}, mapToFaqsPageContent);
  });

  await queryClient.prefetchQuery(
    ['query', faqCategoriesQuery, {}],
    async () => {
      return makeQuery(faqCategoriesQuery, {}, mapToFaqCategories);
    }
  );

  await queryClient.prefetchInfiniteQuery(
    ['pagination', faqsListQuery, {}],
    async () => {
      return makePaginatedQuery(
        faqsListQuery,
        {},
        1,
        7,
        mapToPaginatedFaqsList
      );
    }
  );

  queryClient.setQueryData(['pagination', faqsListQuery, {}], (data) => {
    return Object.assign({}, data, { pageParams: [] });
  });

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 60,
  };
};
